import React, { useEffect } from "react";
import { X } from "lucide-react";
import { hexA } from "@/lib/storeutils";

// Small themed building blocks for the customer account pages (colours come from the store's theme).
export const inr = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const fmtDate = (iso) => { const d = iso ? new Date(iso) : null; return d && !Number.isNaN(d.getTime()) ? `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : "—"; };
export const initials = (n = "") => n.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";

export function Card({ s, className = "", children, ...rest }) {
  return <div className={`rounded-2xl ${className}`} style={{ background: s.c.surface, border: `1px solid ${s.border}`, boxShadow: s.cardShadow }} {...rest}>{children}</div>;
}

export function Field({ s, label, error, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: s.muted }}>{label}</span>
      <div className="mt-1.5">{children}</div>
      {error ? <p role="alert" className="mt-1 text-xs text-red-600">{error}</p> : hint ? <p className="mt-1 text-xs" style={{ color: s.muted }}>{hint}</p> : null}
    </label>
  );
}

export const inputStyle = (s, err) => ({ background: s.c.bg, borderColor: err ? "#dc2626" : s.border, color: s.c.text });
export const inputCls = "w-full rounded-xl px-4 py-3 outline-none border transition-colors text-[14px]";

export function Button({ s, variant = "solid", className = "", children, ...rest }) {
  const style = variant === "solid" ? { background: s.c.primary, color: "#fff" } : variant === "accent" ? { background: s.c.accent, color: "#fff" }
    : variant === "danger" ? { background: "#fff", color: "#dc2626", border: "1px solid #fecaca" } : { background: "transparent", color: s.c.text, border: `1px solid ${s.border}` };
  return <button className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-opacity hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2 ${className}`} style={style} {...rest}>{children}</button>;
}

export function Modal({ s, open, onClose, title, children, footer, testId, wide }) {
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", esc); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={title} data-testid={testId} className={`w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] flex flex-col rounded-2xl`} style={{ background: s.c.surface, color: s.c.text, fontFamily: s.font }}>
        <div className="px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: s.border }}>
          <h2 className="font-bold text-lg">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="p-1 rounded-lg opacity-70 hover:opacity-100"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 overflow-y-auto">{children}</div>
        {footer && <div className="px-6 py-4 flex justify-end gap-2 border-t" style={{ borderColor: s.border }}>{footer}</div>}
      </div>
    </div>
  );
}

const TONES = { good: ["#16a34a", "#dcfce7"], warn: ["#ea580c", "#ffedd5"], bad: ["#dc2626", "#fee2e2"], info: ["#2563eb", "#dbeafe"], mute: ["#64748b", "#f1f5f9"] };
export function Badge({ tone = "mute", children }) {
  const [fg, bg] = TONES[tone];
  return <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ color: fg, background: bg }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: fg }} />{children}</span>;
}
export const orderTone = (st) => (st === "delivered" ? "good" : st === "cancelled" || st === "refunded" ? "bad" : st === "pending" ? "warn" : "info");
export const payInfo = (o) => {
  if (o.payment_status === "refunded" || o.status === "refunded") return ["Refunded", "bad"];
  if (o.status === "cancelled") return ["Cancelled", "mute"];
  if (o.payment_status === "paid") return ["Paid", "good"];
  if (o.payment_status === "cod") return [o.status === "delivered" ? "Paid (COD)" : "Pay on delivery", o.status === "delivered" ? "good" : "warn"];
  return ["Payment pending", "warn"];
};
export const pretty = (v) => (v || "").replace(/_/g, " ").replace(/^./, (m) => m.toUpperCase());
export { hexA };
